import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { toast } from 'components/ui/toast-store'

/**
 * Stop someone walking away from unsaved edits (MYD-495).
 *
 * The app uses `BrowserRouter`, not a data router, so react-router's
 * `useBlocker` is not available. This covers the two exits that matter:
 *
 *  - reload / close tab / typed URL: the browser's own `beforeunload` prompt.
 *  - an in-app LINK (the sidebar, the header, the back link): a capture-phase
 *    click listener on the document runs before React Router's handler, so
 *    it can cancel the click, ask, and navigate itself if the answer is yes.
 *
 * NOT covered: the browser's Back button. Intercepting popstate without a data
 * router means fighting the history stack, which is worse than the risk. The
 * Present button and Save are the editor's own paths and navigate directly.
 */
export function useUnsavedChangesGuard(dirty: boolean, message = 'You have unsaved changes. Leave without saving?') {
	const navigate = useNavigate()
	const dirtyRef = useRef(dirty)
	dirtyRef.current = dirty

	useEffect(() => {
		const beforeUnload = (event: BeforeUnloadEvent) => {
			if (!dirtyRef.current) return
			event.preventDefault()
			// Chrome still wants returnValue set to show the prompt.
			event.returnValue = ''
		}

		const onClick = (event: MouseEvent) => {
			if (!dirtyRef.current || event.defaultPrevented || event.button !== 0) return
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

			const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
			if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return

			const url = new URL(anchor.href, window.location.href)
			if (url.origin !== window.location.origin) return
			const to = `${url.pathname}${url.search}${url.hash}`
			if (to === `${window.location.pathname}${window.location.search}${window.location.hash}`) return

			event.preventDefault()
			event.stopPropagation()
			toast
				.confirm({ title: 'Leave the editor?', message, confirmLabel: 'Leave', variant: 'warning' })
				.then(leave => {
					if (leave) navigate(to)
				})
		}

		window.addEventListener('beforeunload', beforeUnload)
		document.addEventListener('click', onClick, true)
		return () => {
			window.removeEventListener('beforeunload', beforeUnload)
			document.removeEventListener('click', onClick, true)
		}
	}, [message, navigate])
}

export default useUnsavedChangesGuard
